import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'

import { date, type Dayjs } from 'configuration/dayjs'
import {
    ADVANCED_TEXT,
    ADVANCED_PRODUCT_TITLE,
    BRANDS,
    CAMPAIGN_STATES,
    CAMPAIGN_TARGETING_TYPES,
    CAMPAIGNS,
    COUNTRIES,
    DATES,
    EQUALS,
    FACT_TYPES,
    LABELS,
    METRIC_FILTERS,
    PARENT_ASINS,
    PORTFOLIOS,
    PRODUCT_AD_STATES,
    PRODUCT_ADS,
    PRODUCT_ASINS,
    PRODUCT_NUMBER_OF_ITEMS,
    PRODUCT_SKUS,
    REGIONS,
} from 'const/filters'
import { PRODUCT_LABELS } from 'const/resourceTypes'
import { formatCerebroDate } from 'helpers/formatting'
import type { FilterOption, FiltersState } from 'types'

import { presetRanges } from '../dateRange'
import { formatBooleanFilter } from '../filters'

import { formatMetricFiltersForParams } from './metrics'

export const formatFilters = (
    filters: FiltersState | undefined,
    _hasHourlyData = false,
    fiscalYearStartMonth?: number
): Record<string, string | number> => {
    let params: Record<string, string | number> = {}
    if (!filters) {
        return params
    }

    if (!isEmpty(filters[DATES])) {
        if (isArray(filters[DATES])) {
            const dates = filters[DATES]
            params.report_date_min = formatCerebroDate(dates[0])
            params.report_date_max = formatCerebroDate(dates[1])
        } else {
            const presetMap = presetRanges(
                date(),
                filters.rangeLag,
                fiscalYearStartMonth
            ) as Record<string, { range: [Dayjs, Dayjs] }>
            const { range } = presetMap[filters[DATES] as string]
            params.report_date_min = formatCerebroDate(range[0])
            params.report_date_max = formatCerebroDate(range[1])
        }
    }

    if (!isEmpty(filters[REGIONS])) {
        const items = filters[REGIONS]!
        params.profile__region__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[COUNTRIES])) {
        const items = filters[COUNTRIES]!
        params.profile__country_code__in = items
            .map((item) => item.value)
            .join()
    }

    if (!isEmpty(filters[BRANDS])) {
        const items = filters[BRANDS]!
        params.profile__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[PORTFOLIOS])) {
        const items = filters[PORTFOLIOS]!
        params.campaign__portfolio__id__in = items
            .map((item) => item.value)
            .join()
    }

    if (!isEmpty(filters[CAMPAIGNS])) {
        const items = filters[CAMPAIGNS]!
        params.campaign__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[CAMPAIGN_STATES])) {
        const items = filters[CAMPAIGN_STATES]!
        params.campaign__state__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[CAMPAIGN_TARGETING_TYPES])) {
        const items = filters[CAMPAIGN_TARGETING_TYPES]!
        params.campaign__targeting_type__in = items
            .map((item) => item.value)
            .join()
    }

    if (!isEmpty(filters[ADVANCED_PRODUCT_TITLE])) {
        params.boolean_filter = formatBooleanFilter(
            filters[ADVANCED_PRODUCT_TITLE],
            ADVANCED_PRODUCT_TITLE
        )
    }

    if (!isEmpty(filters[PRODUCT_NUMBER_OF_ITEMS])) {
        const { operator, value } = filters[PRODUCT_NUMBER_OF_ITEMS]![0] as {
            operator: string | null
            value: string | number | null
        }
        if (!isNull(operator) && !isNull(value)) {
            let key = 'product_ad__metadata__number_of_items'
            if (operator !== EQUALS) {
                key += `__${operator}`
            }
            params[key] = value
        }
    }

    if (!isEmpty(filters[PRODUCT_ASINS])) {
        const items = filters[PRODUCT_ASINS]!
        params.product_ad__asin__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[PRODUCT_SKUS])) {
        const items = filters[PRODUCT_SKUS]!
        params.product_ad__sku__in = items.map((item) => item.value).join()
    }

    const parentAsins = (filters as Record<string, unknown>)[PARENT_ASINS]
    if (!isEmpty(parentAsins)) {
        const items = parentAsins as FilterOption[]
        params.product_ad__metadata__parent_asin__in = items
            .map((item) => item.value)
            .join()
    }

    if (!isEmpty(filters[PRODUCT_ADS])) {
        const items = filters[PRODUCT_ADS]!
        params.product_ad__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[PRODUCT_AD_STATES])) {
        const items = filters[PRODUCT_AD_STATES]!
        params.product_ad__state__in = items.map((item) => item.value).join()
    }

    if (!isEmpty(filters[LABELS])) {
        params.complex_label_filter = formatBooleanFilter(
            filters[LABELS],
            PRODUCT_LABELS
        )
    }

    if (!isEmpty(filters[ADVANCED_TEXT])) {
        params.complex_text_filter = formatBooleanFilter(filters[ADVANCED_TEXT])
    }

    const metricFilters = filters[METRIC_FILTERS]
    if (!isEmpty(metricFilters) && Array.isArray(metricFilters)) {
        params = {
            ...params,
            ...formatMetricFiltersForParams(metricFilters),
        } as Record<string, string | number>
    }

    if (!isEmpty(filters[FACT_TYPES])) {
        const items = filters[FACT_TYPES]!
        params.campaign__campaign_type__in = items
            .map((item) => item.value)
            .join()
    }

    return params
}
